import trapFocus from '../../utils/trapFocus';
import { BREAKPOINTS, SETTINGS, CLASSES } from '../../utils/constants';
import { refreshDeferredFacets, scheduleDeferredFacetRefresh } from './refreshDeferredFacets';

const PLP_FILTERS_OPEN_CLASS = 'collection-plp--filters-open';
const PLP_SCOPE_SELECTOR = '[data-filters-drawer-scope]';
const DEFERRED_FILTER_FORM_SELECTOR = '[data-filter-form][data-filter-deferred-submit]';

const isMobileFilterViewport = () =>
  window.matchMedia(`(max-width: ${BREAKPOINTS.DESKTOP - 1}px)`).matches;

const bindFilterFormAutoSubmit = (form, { useDeferredAjax = false } = {}) => {
  if (!form || form.dataset.filterAutoSubmit === 'true') return;
  form.dataset.filterAutoSubmit = 'true';
  form.addEventListener('change', e => {
    const target = e.target;
    if (!(target instanceof HTMLInputElement) && !(target instanceof HTMLSelectElement)) return;
    if (useDeferredAjax && form.hasAttribute('data-filter-deferred-submit')) {
      scheduleDeferredFacetRefresh(form);
      return;
    }
    if (target.matches('input[type="checkbox"], input[type="number"], input[type="radio"]')) {
      form.requestSubmit();
    }
  });
};

const initCollectionPlpMobileFilterDrawer = () => {
  document.querySelectorAll(`${PLP_SCOPE_SELECTOR}[data-collection-mobile-drawer]`).forEach(scope => {
    if (scope.dataset.collectionPlpDrawerInit === 'true') {
      return;
    }
    scope.dataset.collectionPlpDrawerInit = 'true';

    const openBtn = scope.querySelector('[data-open-filter-drawer]');
    const aside = scope.querySelector('[data-collection-filters-aside]');
    const backdrop = scope.querySelector('[data-collection-filters-backdrop]');
    if (!openBtn || !aside || !backdrop) {
      return;
    }

    const onOpen = () => {
      scope.classList.add(PLP_FILTERS_OPEN_CLASS);
      document.body.classList.add(CLASSES.OVERFLOW_HIDDEN);
      openBtn.setAttribute('aria-expanded', 'true');
    };

    const onClose = () => {
      scope.classList.remove(PLP_FILTERS_OPEN_CLASS);
      document.body.classList.remove(CLASSES.OVERFLOW_HIDDEN);
      openBtn.setAttribute('aria-expanded', 'false');
    };

    openBtn.addEventListener('click', () => {
      if (scope.classList.contains(PLP_FILTERS_OPEN_CLASS)) {
        onClose();
      } else {
        onOpen();
      }
    });

    backdrop.addEventListener('click', onClose);
    scope.querySelectorAll('[data-close-filter-drawer-button]').forEach(btn => {
      btn.addEventListener('click', onClose);
    });

    const media = window.matchMedia(`(min-width: ${BREAKPOINTS.DESKTOP}px)`);
    const onMq = () => {
      if (media.matches) {
        onClose();
      }
    };
    media.addEventListener('change', onMq);

    document.addEventListener('keydown', e => {
      if (e.key === 'Escape' && scope.classList.contains(PLP_FILTERS_OPEN_CLASS)) {
        onClose();
      }
    });

    scope.querySelectorAll(DEFERRED_FILTER_FORM_SELECTOR).forEach(form => {
      form.addEventListener('submit', async e => {
        if (!form.hasAttribute('data-filter-deferred-submit')) return;
        e.preventDefault();
        try {
          await refreshDeferredFacets(form);
        } finally {
          if (isMobileFilterViewport()) {
            onClose();
          }
        }
      });
    });
  });
};

const initCollectionPlpAjaxLinkDelegation = () => {
  document.querySelectorAll(`${PLP_SCOPE_SELECTOR}[data-section-id]`).forEach(scope => {
    if (scope.dataset.collectionPlpAjaxLinksInit === 'true') return;
    if (!scope.querySelector(DEFERRED_FILTER_FORM_SELECTOR)) return;
    scope.dataset.collectionPlpAjaxLinksInit = 'true';

    scope.addEventListener('change', e => {
      const t = e.target;
      if (!(t instanceof HTMLSelectElement) || t.name !== 'sort_by') return;
      if (!t.closest('form.collection-sort')) return;
      const form = scope.querySelector(DEFERRED_FILTER_FORM_SELECTOR);
      if (form) scheduleDeferredFacetRefresh(form);
    });

    scope.addEventListener('click', e => {
      const a = e.target.closest('a.filter-chip, a[data-filters-clear-all]');
      if (!a?.href) return;
      e.preventDefault();
      const form = scope.querySelector(DEFERRED_FILTER_FORM_SELECTOR);
      if (!form) return;
      refreshDeferredFacets(form, { filterSourceUrl: a.href });
    });
  });
};

const initCollectionSearchFilters = () => {
  document.querySelectorAll('[data-filters-container]').forEach(filtersContainer => {
    const filterForm = filtersContainer.querySelector('[data-filter-form]');
    const isSidebarLayout = filtersContainer.dataset.filtersLayout === 'sidebar';
    bindFilterFormAutoSubmit(filterForm, { useDeferredAjax: isSidebarLayout });

    if (isSidebarLayout) {
      return;
    }

    const filterDrawer = filtersContainer.querySelector('[data-filter-drawer]');
    const openDrawerBtn = filtersContainer.querySelector('[data-open-filter-drawer]');
    const closeDrawerBtn = filtersContainer.querySelector('[data-close-filter-drawer-button]');

    if (!filterDrawer || !openDrawerBtn) return;

    const closeFilterDrawer = () => {
      filterDrawer.classList.remove(CLASSES.TRANSLATE_X_0);
      setTimeout(() => {
        filterDrawer.close();
      }, SETTINGS.FILTER_DRAW_ANIMATION_TIME);
    };

    const handleCloseFilterDrawerClick = () => {
      closeFilterDrawer();
    };

    const handleFilterDrawerClick = e => {
      if (!filterDrawer) return;
      const isInDialog = e.clientX >= filterDrawer.getBoundingClientRect().left;
      if (!isInDialog) {
        closeFilterDrawer();
      }
    };

    const openFilterDrawer = () => {
      filterDrawer.showModal();
      requestAnimationFrame(() => {
        filterDrawer.classList.add(CLASSES.TRANSLATE_X_0);
      });
      trapFocus(filterDrawer);
      filterDrawer.addEventListener('click', handleFilterDrawerClick);
      closeDrawerBtn?.addEventListener('click', handleCloseFilterDrawerClick);
    };

    openDrawerBtn.addEventListener('click', () => openFilterDrawer());

    filterDrawer.addEventListener('close', () => {
      filterDrawer.removeEventListener('click', handleFilterDrawerClick);
      closeDrawerBtn?.removeEventListener('click', handleCloseFilterDrawerClick);
      openDrawerBtn.focus();
    });
  });

  initCollectionPlpMobileFilterDrawer();
  initCollectionPlpAjaxLinkDelegation();
};

export default initCollectionSearchFilters;
